import * as zod from 'zod';

import { DemonstrationReferenceFrame } from './demonstrationRecording';

export const MAX_RECENT_EXECUTIONS = 10;

/**
 * Mirror of routine-runner's `Detection2D` box
 * (libs/routine-runner/vision/LocateTypes.ts), in full-frame pixel
 * coordinates. Deliberately lean: the SAM2 `segment` (a `Blob2D` with
 * heavyweight extras — mask RLE, intensity stats) is not persisted; only its
 * contour rides separately on `LeanLocateMatch`. Duplicated here (rather than
 * imported) so the persisted wire shape can't drift when the runner type
 * evolves.
 */
export const LeanDetection = zod.object({
  x: zod.number(),
  y: zod.number(),
  width: zod.number(),
  height: zod.number(),
  rotation: zod.number(), // radians
  score: zod.number(),
  className: zod.string().optional(),
  classId: zod.number().optional(),
});

export type LeanDetection = zod.infer<typeof LeanDetection>;

export const LeanLocateMatch = zod.object({
  detection: LeanDetection,
  /**
   * SAM2 contour from the runner's `LocateResult.segment.contour`, full-frame
   * pixel coords. Empty when segmentation was off
   * (`enableSegmentationMask=false`) or produced no contour — consumers fall
   * back to the detection box.
   */
  contour: zod
    .array(zod.object({ x: zod.number(), y: zod.number() }))
    .default([]),
});

export type LeanLocateMatch = zod.infer<typeof LeanLocateMatch>;

export const SkillExecutionRecord = zod.object({
  /** ISO 8601 */
  timestamp: zod.string(),
  /** Feathers storage blob id; null when the image upload failed. */
  imageStorageID: zod.string().nullable(),
  /** Natural frame size in px — required to scale overlays at display time. */
  imageWidth: zod.number(),
  imageHeight: zod.number(),
  matches: zod.array(LeanLocateMatch).default([]),
});

export type SkillExecutionRecord = zod.infer<typeof SkillExecutionRecord>;

/**
 * Handoff from routine-runner's RunLocateSkill step to botman's persister.
 * The raw PNG rides along; botman uploads it and builds the final
 * `SkillExecutionRecord`.
 *
 * Plain TS type (not a zod schema) — `image: ArrayBuffer` is not serializable,
 * so runtime validation isn't applied here.
 */
export type LocateExecutionUpdate = {
  skillId: string;
  /**
   * Raw camera frame as returned by `runLocate()` (typically JPEG). Uploaded
   * as-is; botman sniffs the magic bytes for the stored content type.
   */
  image: ArrayBuffer;
  timestamp: string;
  imageWidth: number;
  imageHeight: number;
  matches: LeanLocateMatch[];
};

// Mirrors `equipmentAssignmentValue` from @sb/ai/types (cannot import due to
// eslint no-restricted-imports on @sb/* in feathers-types). Keep the two
// unions textually aligned — a bare equipment id (legacy shorthand), or an
// object carrying an optional lens `feed` for dual-lens cameras.
const equipmentAssignmentValueSchema = zod.union([
  zod.string(),
  zod.object({
    equipmentId: zod.string(),
    feed: zod.enum(['front', 'back']).optional(),
  }),
]);

// Mirrors HardwareConfigEquipmentMapping from @sb/ai/types (cannot import due
// to eslint no-restricted-imports on @sb/* in feathers-types).
const HardwareConfigEquipmentMappingSchema = zod.record(
  zod.string(),
  zod.object({
    robotId: zod.string().optional(),
    robotEquipmentId: zod.string().optional(),
    hardwareKeyToEquipmentId: zod.record(
      zod.string(),
      equipmentAssignmentValueSchema,
    ),
  }),
);

// Mirrors HardwareConfigSchema + HardwareConfigItem from @sb/ai/types. Captured
// on the skill at creation time so the skill is self-describing — frontends can
// render the hardware-mapping UI without a second fetch, and edits to the
// source HardwareConfig row don't retroactively change an existing skill.
const HardwareConfigSnapshotSchema = zod.object({
  id: zod.string(),
  name: zod.string(),
  config: zod.object({
    robots: zod.record(
      zod.string(),
      zod.object({
        isPrimary: zod.boolean(),
        hardware: zod.record(zod.string(), zod.object({ id: zod.string() })),
      }),
    ),
  }),
});

export const SkillData = zod.object({
  id: zod.string(),
  name: zod.string(),
  description: zod.string().nullable().optional(),
  icon: zod.string().nullable().optional(),
  skillType: zod.enum(['control', 'locate', 'classify', 'agent', 'ai-motion']),
  method: zod.string(),
  hardwareConfigEquipmentMapping: HardwareConfigEquipmentMappingSchema.default(
    {},
  ),
  hardwareConfig: HardwareConfigSnapshotSchema.nullable().optional(),
  // Permissive envelope; concrete shape is validated by @sb/ai/types. Kept a
  // plain record here since feathers-types can't import @sb/* (lint rule).
  shared: zod.record(zod.string(), zod.unknown()).default({}),
  methodConfig: zod.record(zod.string(), zod.unknown()).default({}),
  // Heavy rolling history. NOTE: the skills service strips this field from
  // responses and published events unless the request opts in with
  // `query: { withRecentExecutions: true }` — wire payloads cast to
  // `SkillData` may omit it despite the non-optional inferred type.
  recentExecutions: zod
    .array(SkillExecutionRecord)
    .max(MAX_RECENT_EXECUTIONS)
    .default([]),
  createdAt: zod.string().optional(),
  updatedAt: zod.string().optional(),
});

export type SkillData = zod.infer<typeof SkillData>;

export const DemonstrationSkillConfig = zod.object({
  // Functional default — Zod evaluates a literal `[]` default once at schema
  // build time, so every parse without `recordings` would share the same
  // array reference. A factory hands each parse its own fresh array.
  recordings: zod.array(zod.string()).default(() => []),
  referenceFrame: DemonstrationReferenceFrame.nullable().optional(),
  // Optional Locate skill paired with this ShowPath. When set, the
  // RunDemonstration step view defaults its `locateSkillId` dropdown to this
  // skill so the operator doesn't have to re-pick it for every routine step.
  // Nullable so the operator can explicitly clear it.
  associatedLocateSkillId: zod.string().nullable().optional(),
});

export type DemonstrationSkillConfig = zod.infer<
  typeof DemonstrationSkillConfig
>;

export function parseDemonstrationSkill(
  skill: SkillData,
): DemonstrationSkillConfig {
  if (skill.skillType !== 'ai-motion') {
    throw new Error(
      `parseDemonstrationSkill: skill ${skill.id} has skillType=${skill.skillType}, expected 'ai-motion'`,
    );
  }

  return DemonstrationSkillConfig.parse(skill.shared);
}
